export default function DevelopersSection() {
  const features = [
    {
      icon: "ri-terminal-box-line",
      title: "RESTful APIs",
      desc: "Clean, predictable, and fully documented REST APIs for every payment workflow.",
    },
    {
      icon: "ri-code-s-slash-line",
      title: "SDKs & Libraries",
      desc: "Official SDKs for Node.js, Python, PHP, Ruby, Go, Java, and more.",
    },
    {
      icon: "ri-webhook-line",
      title: "Webhooks",
      desc: "Real-time event notifications with retry logic and delivery guarantees.",
    },
    {
      icon: "ri-test-tube-line",
      title: "Sandbox Testing",
      desc: "Full-featured test environment to simulate any payment scenario.",
    },
  ];

  return (
    <section className="py-16 md:py-24" style={{ background: "#F5F7FB" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-100 mb-5 tracking-wide">
              <i className="ri-code-line w-3.5 h-3.5 flex items-center justify-center" />
              FOR DEVELOPERS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Build faster with<br />
              <span style={{ background: "linear-gradient(135deg, #2563EB, #14B8A6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                world-class tooling
              </span>
            </h2>
            <p className="text-gray-500 text-lg mb-8 leading-relaxed">
              From your first API call to processing millions — Paygent developer experience is designed for speed, reliability, and scale.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {features.map((f) => (
                <div key={f.title} className="flex gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg"
                    style={{ background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" }}>
                    <i className={`${f.icon} text-blue-600 text-sm w-4 h-4 flex items-center justify-center`} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-0.5">{f.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="/developers/docs" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ boxShadow: "0 4px 14px rgba(37,99,235,0.2)" }}
              >
                Read the Docs
              </a>
              <a href="/developers/sdk" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 border border-gray-200 bg-white rounded-xl hover:border-blue-300 hover:text-blue-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                View API Reference
              </a>
            </div>
          </div>

          {/* Right: Terminal */}
          <div className="rounded-2xl overflow-hidden" style={{ background: "#0F172A", boxShadow: "0 16px 48px rgba(0,0,0,0.15)" }}>
            {/* Terminal header */}
            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10">
              <div className="w-3 h-3 rounded-full bg-red-400/70" />
              <div className="w-3 h-3 rounded-full bg-yellow-400/70" />
              <div className="w-3 h-3 rounded-full bg-green-400/70" />
              <span className="text-gray-400 text-xs ml-3 font-mono">paygent-quickstart.js</span>
            </div>
            <div className="p-6 font-mono text-sm">
              <div className="space-y-1">
                <p><span className="text-purple-400">const</span> <span className="text-blue-300">paygent</span> <span className="text-gray-400">=</span> <span className="text-yellow-300">require</span><span className="text-gray-400">(</span><span className="text-green-300">&apos;paygent&apos;</span><span className="text-gray-400">)(</span><span className="text-green-300">&apos;pgt_live_key&apos;</span><span className="text-gray-400">);</span></p>
                <p className="h-3" />
                <p><span className="text-gray-500">// Create a payment intent</span></p>
                <p><span className="text-purple-400">const</span> <span className="text-blue-300">intent</span> <span className="text-gray-400">=</span> <span className="text-purple-400">await</span> <span className="text-blue-300">paygent</span><span className="text-gray-400">.</span><span className="text-yellow-300">paymentIntents</span><span className="text-gray-400">.</span><span className="text-yellow-300">create</span><span className="text-gray-400">({"{"}</span></p>
                <p className="pl-4"><span className="text-blue-200">amount</span><span className="text-gray-400">: </span><span className="text-orange-300">25000</span><span className="text-gray-400">,</span></p>
                <p className="pl-4"><span className="text-blue-200">currency</span><span className="text-gray-400">: </span><span className="text-green-300">&apos;usd&apos;</span><span className="text-gray-400">,</span></p>
                <p className="pl-4"><span className="text-blue-200">metadata</span><span className="text-gray-400">: {"{ "}</span><span className="text-blue-200">orderId</span><span className="text-gray-400">: </span><span className="text-green-300">&apos;ord_1234&apos;</span> <span className="text-gray-400">{"}"},</span></p>
                <p><span className="text-gray-400">{"})"};</span></p>
                <p className="h-3" />
                <p><span className="text-gray-500">// Confirm the payment</span></p>
                <p><span className="text-purple-400">const</span> <span className="text-blue-300">result</span> <span className="text-gray-400">=</span> <span className="text-purple-400">await</span> <span className="text-blue-300">paygent</span><span className="text-gray-400">.</span><span className="text-yellow-300">paymentIntents</span><span className="text-gray-400">.</span><span className="text-yellow-300">confirm</span><span className="text-gray-400">(</span></p>
                <p className="pl-4"><span className="text-blue-300">intent</span><span className="text-gray-400">.</span><span className="text-blue-200">id</span></p>
                <p><span className="text-gray-400">);</span></p>
                <p className="h-3" />
                <p><span className="text-gray-500">// {"{ status: 'succeeded', amount: 25000 }"}</span></p>
              </div>
              {/* Blinking cursor */}
              <div className="mt-4 flex items-center gap-1">
                <span className="text-green-400">$</span>
                <span className="w-2 h-4 bg-blue-400 animate-pulse" />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <span className="text-xs text-gray-400">Payment succeeded — 482ms</span>
              </div>
              <span className="text-xs text-blue-400 font-semibold cursor-pointer hover:text-blue-300">Copy code</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
